import React, { useEffect, useState } from "react";
import { Autocomplete, Box, Paper, TextField, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { Page } from "../../../components/page-base";
import { Container } from "../../sellers/pages/styles";
import useUserStore from "../../../store/user";
import { FollowingProfilesService } from "../../following-profiles/services";
import { handleError } from "../../../utils/parseApiReturn";
import { Botao, Hint } from "../../profiles-tracking/components/edit-profile-modal/components/EditorChrome";
import { enviarAoPerfil, situacaoDoPerfil, type SituacaoDoPerfil } from "../../../push";

type Opcao = { publicId: string; nome: string };

const MODELOS = [
  { titulo: "Postagem nova no seu perfil", texto: "Publicamos hoje uma postagem nova no seu perfil do Google. Toque para ver como está seu desempenho." },
  { titulo: "Avaliações respondidas", texto: "Respondemos as avaliações mais recentes do seu perfil. Quem pesquisa vê que sua empresa responde." },
  { titulo: "Seu perfil subiu no Google", texto: "Seu perfil melhorou de posição nas pesquisas acompanhadas. Toque para ver o relatório." },
];

const LARGURA = { xs: "100%", md: 880 };

/** Equipe: avisar no celular os clientes de um perfil do que estamos fazendo. */
export const Notificacoes: React.FC = () => {
  const { user } = useUserStore();
  const [busca, setBusca] = useState("");
  const [opcoes, setOpcoes] = useState<Opcao[]>([]);
  const [perfil, setPerfil] = useState<Opcao | null>(null);
  const [situacao, setSituacao] = useState<SituacaoDoPerfil | null>(null);
  const [titulo, setTitulo] = useState("");
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!user?.token) return;
    let vivo = true;
    const espera = window.setTimeout(async () => {
      const dados = await FollowingProfilesService.getFollowingProfiles({ token: user.token, page: 0, size: 20, query: busca });
      if (!vivo || !dados?.content) return;
      setOpcoes(
        dados.content.map((p: { profileBusinessId: string; razaoSocial?: string; name?: string }) => ({
          publicId: p.profileBusinessId,
          nome: p.razaoSocial || p.name || p.profileBusinessId,
        })),
      );
    }, 350);
    return () => {
      vivo = false;
      window.clearTimeout(espera);
    };
  }, [busca, user?.token]);

  const carregarSituacao = async (escolhido: Opcao | null) => {
    setSituacao(null);
    if (!escolhido) return;
    try {
      setSituacao(await situacaoDoPerfil(escolhido.publicId));
    } catch (erro) {
      toast.error(handleError(erro, "Não foi possível carregar o perfil.").message);
    }
  };

  const enviar = async () => {
    if (!perfil) return;
    setEnviando(true);
    try {
      const r = await enviarAoPerfil(perfil.publicId, { title: titulo.trim(), body: texto.trim() });
      if (r.aparelhos === 0) {
        toast.info("Enviado, mas nenhum cliente deste perfil ativou as notificações ainda.");
      } else {
        toast.success(`Notificação entregue em ${r.entregues} de ${r.aparelhos} aparelho(s).`);
      }
      setTitulo("");
      setTexto("");
      await carregarSituacao(perfil);
    } catch (erro) {
      toast.error(handleError(erro, "Não foi possível enviar a notificação.").message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Page.Root>
      <Container id="dashboard-container">
        <Page.HeaderAux />
        <Page.Header
          title="Notificações"
          subtitle="Avise no celular os clientes de um perfil sobre o que estamos fazendo. Todo fim de dia o sistema também manda sozinho o resumo do trabalho feito."
        />

        <Box sx={{ width: LARGURA, mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
            <Autocomplete
              options={opcoes}
              value={perfil}
              getOptionLabel={(o) => o.nome}
              isOptionEqualToValue={(a, b) => a.publicId === b.publicId}
              filterOptions={(x) => x}
              onInputChange={(_, valor) => setBusca(valor)}
              onChange={(_, escolhido) => {
                setPerfil(escolhido);
                carregarSituacao(escolhido);
              }}
              noOptionsText="Nenhum perfil encontrado"
              renderInput={(params) => <TextField {...params} label="Perfil" placeholder="Buscar pelo nome" />}
            />
            {situacao && (
              <Typography sx={{ mt: 1.5, fontSize: 14 }} role="status">
                {situacao.clientes === 0
                  ? "Este perfil não tem cliente com acesso. Libere em Equipe e acessos."
                  : `${situacao.clientes} cliente(s) com acesso · ${situacao.clientesComAparelho} com notificação ativada · ${situacao.aparelhos} aparelho(s)`}
                {!situacao.ligado && " · as notificações estão desligadas no servidor"}
              </Typography>
            )}
          </Paper>

          {perfil && (
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {MODELOS.map((m) => (
                  <Botao
                    key={m.titulo}
                    type="button"
                    $variante="tonal"
                    style={{ height: 32, fontSize: 13 }}
                    onClick={() => {
                      setTitulo(m.titulo);
                      setTexto(m.texto);
                    }}
                  >
                    {m.titulo}
                  </Botao>
                ))}
              </Box>
              <TextField
                label="Título"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                inputProps={{ maxLength: 60 }}
                helperText={`${titulo.length}/60`}
              />
              <TextField
                label="Mensagem"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                inputProps={{ maxLength: 240 }}
                helperText={`${texto.length}/240 · ao tocar, o cliente abre o relatório deste perfil`}
                multiline
                minRows={3}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Botao
                  type="button"
                  $variante="filled"
                  onClick={enviar}
                  disabled={enviando || !titulo.trim() || !texto.trim() || !situacao?.ligado}
                >
                  <span className="material-icons" aria-hidden="true">send</span>
                  {enviando ? "Enviando..." : "Enviar notificação"}
                </Botao>
                <Hint>Só fale do que foi feito de verdade: a mensagem chega no celular do cliente.</Hint>
              </Box>
            </Paper>
          )}

          {situacao && situacao.historico.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Últimos envios</Typography>
              {situacao.historico.map((h) => (
                <Box key={h.enviadaEm + h.titulo} sx={{ py: 1, borderTop: "1px solid #EEF1F4" }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                    {h.titulo}
                    <Typography component="span" sx={{ fontSize: 12, color: "#5F6368", ml: 1 }}>
                      {new Date(h.enviadaEm).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                      {" · "}
                      {h.tipo === "RESUMO_DIARIO" ? "resumo automático" : "enviada pela equipe"}
                      {` · entregue em ${h.entregues}/${h.aparelhos}`}
                    </Typography>
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#3C4043" }}>{h.texto}</Typography>
                </Box>
              ))}
            </Paper>
          )}
        </Box>
      </Container>
    </Page.Root>
  );
};

export default Notificacoes;
